import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { getGuide, GUIDES } from "@/data/guides";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Clock, ArrowLeft, Lightbulb, Calculator } from "lucide-react";

export default function GuideArticle() {
  const { slug } = useParams();
  const guide = getGuide(slug);

  useEffect(() => {
    if (guide) {
      document.title = `${guide.title} — Peak Points`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", guide.dek);
    }
    return () => { document.title = "Peak Points — Know what your points are worth"; };
  }, [guide]);

  if (!guide) return <Navigate to="/guides" replace />;

  const related = GUIDES.filter((g) => g.slug !== guide.slug).slice(0, 2);

  return (
    <PublicLayout>
      <article className="mx-auto max-w-3xl px-6 py-14 md:py-20 lg:px-8" data-testid="guide-article">
        <Link to="/guides" className="inline-flex items-center gap-1 font-heading text-sm font-semibold text-slate-500 hover:text-primary" data-testid="back-to-guides">
          <ArrowLeft className="h-4 w-4" /> All guides
        </Link>

        <div className="mt-6 flex items-center gap-3 font-heading text-xs font-bold uppercase tracking-wide text-slate-400">
          <span className="rounded-full bg-primary/10 px-2.5 py-1 text-primary">{guide.category}</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {guide.readTime}</span>
          <span>Updated {guide.updated}</span>
        </div>

        <h1 className="mt-4 font-heading text-3xl font-black tracking-tighter text-slate-900 sm:text-4xl">
          {guide.title}
        </h1>
        <p className="mt-4 font-body text-lg leading-relaxed text-slate-600">{guide.dek}</p>

        <div className="mt-10 space-y-10">
          {guide.sections.map((s, i) => (
            <motion.section
              key={s.heading}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.03 }}
            >
              <h2 className="font-heading text-xl font-bold tracking-tight text-slate-900">{s.heading}</h2>
              {s.body.map((p, j) => (
                <p key={j} className="mt-3 font-body leading-relaxed text-slate-600">{p}</p>
              ))}
            </motion.section>
          ))}
        </div>

        {/* Takeaway */}
        <div className="mt-12 rounded-3xl bg-accent/10 p-6" data-testid="guide-takeaway">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/20 text-emerald-700">
              <Lightbulb className="h-5 w-5" />
            </span>
            <div>
              <p className="font-heading text-xs font-bold uppercase tracking-[0.15em] text-emerald-700">Takeaway</p>
              <p className="mt-1 font-body leading-relaxed text-slate-700">{guide.takeaway}</p>
            </div>
          </div>
        </div>

        {/* Calculator CTA */}
        <div className="mt-8 flex flex-col items-start justify-between gap-4 rounded-3xl border border-border bg-card p-6 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Calculator className="h-5 w-5" />
            </span>
            <div>
              <p className="font-heading font-bold text-slate-900">See what your points are worth</p>
              <p className="font-body text-sm text-slate-500">Free calculator, no signup required.</p>
            </div>
          </div>
          <Link to="/" data-testid="guide-calculator-cta">
            <Button className="rounded-full bg-primary px-6 py-5 font-heading font-bold text-primary-foreground hover:bg-blue-700">
              Open calculator
            </Button>
          </Link>
        </div>

        {/* Related */}
        <div className="mt-14">
          <p className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Keep reading</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {related.map((g) => (
              <Link key={g.slug} to={`/guides/${g.slug}`} data-testid={`related-guide-${g.slug}`} className="group rounded-2xl border border-border bg-card p-5 transition-transform duration-200 hover:-translate-y-0.5 hover:border-primary/40">
                <h3 className="font-heading font-bold text-slate-900 group-hover:text-primary">{g.title}</h3>
                <p className="mt-1 font-body text-sm text-slate-500">{g.dek}</p>
              </Link>
            ))}
          </div>
        </div>
      </article>
    </PublicLayout>
  );
}
